import { useQuery } from '@tanstack/react-query';
import type { WorkflowKit } from '@lixionary-jira/domain';
import { Copy, Download } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { ProjectSettings } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button, LinkButton } from '../../ui/Button';
import { Alert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Segmented } from '../../ui/forms';
import { Card, PageHeader, usePageTitle } from '../../ui/misc';
import { SettingsNav } from './SettingsNav';
import { WorkflowCheck } from './WorkflowCheck';

type PromptKind = 'plugin' | 'any';

/**
 * The project's GitHub workflow kit (deploy workflow contract): its state, a ZIP download, the prompt for an AI
 * agent that adapts it to the repository, and the secrets and variables to set.
 */
export function GithubWorkflowsPage() {
  const { key } = useParams();
  const admin = useIsAdmin();
  const project = useProject(key);
  usePageTitle(project.data ? `${project.data.name} · GitHub workflows` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Settings', to: `/projects/${key}/settings` },
    { label: 'GitHub workflows' },
  ]);
  const kit = useQuery({
    queryKey: ['workflow-kit', key],
    queryFn: () => api.get<WorkflowKit>(`/projects/${key}/workflow-kit`),
    enabled: admin,
  });
  const settings = useQuery({
    queryKey: ['settings', key],
    queryFn: () => api.get<ProjectSettings>(`/projects/${key}/settings`),
    enabled: admin,
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="GitHub workflows"
        subtitle="Jira deploys a release by running a GitHub workflow in the project's repository. This kit holds those workflows, filled in for this project, and how to set them up."
        actions={
          <a className="btn btn-primary" href={`/api/v1/projects/${key}/workflow-kit.zip`} download>
            <Download size={16} aria-hidden /> Download kit (.zip)
          </a>
        }
      />
      <SettingsNav projectKey={key!} />
      <QueryView query={kit}>
        {(k) => (
          <>
            {!k.repository && (
              <Alert tone="warning" title="No repository">
                Set the project's GitHub repository under Repository and builders first.
              </Alert>
            )}
            <PromptCard kit={k} />
            <Card
              title="Environments"
              subtitle="Each environment deploys through its own workflow. Required CI checks make Proceed wait for CI on the revision."
              action={
                <LinkButton size="sm" to={`/projects/${key}/settings/environments`}>
                  Edit environments
                </LinkButton>
              }
            >
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Environment</th>
                      <th>Deploy workflow</th>
                      <th>Required CI checks</th>
                      <th>Approval</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {k.environments.map((e) => {
                      const env = settings.data?.environments.find((x) => x.name === e.name);
                      return (
                        <tr key={e.name}>
                          <td>
                            {e.name}
                            <div className="small muted">
                              GitHub environment <span className="mono">{e.githubEnvironment}</span>
                            </div>
                          </td>
                          <td>
                            <span className="mono small">{e.workflow}</span>
                            {e.suggested && (
                              <div>
                                <Badge tone="warning">Not set in Jira yet</Badge>
                              </div>
                            )}
                          </td>
                          <td>
                            {e.requiredChecks.length ? (
                              <span className="mono small">{e.requiredChecks.join(', ')}</span>
                            ) : (
                              <span className="small muted">None</span>
                            )}
                            {e.missingImageCheck && (
                              <div>
                                <Badge tone="warning">Add images</Badge>
                              </div>
                            )}
                          </td>
                          <td className="small">{e.requiresApproval ? 'Required' : 'Not required'}</td>
                          <td>{env && <WorkflowCheck projectKey={key!} env={env} />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card
              title="Secrets and variables"
              subtitle="Set these in the service's GitHub repository. Organization-scoped values can be shared by every service; environment-scoped ones go on each GitHub environment above."
            >
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Kind</th>
                      <th>Scope</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {k.settings.map((s) => (
                      <tr key={s.name}>
                        <td className="mono small">{s.name}</td>
                        <td className="small">{s.kind === 'secret' ? 'Secret' : 'Variable'}</td>
                        <td className="small">
                          {s.scope === 'organization' ? 'Organization' : 'Environment'}
                        </td>
                        <td className="small">
                          {s.required ? '' : <span className="muted">Optional. </span>}
                          {s.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card title="Files in the kit" subtitle="The ZIP holds these, under one folder.">
              <div className="stack sm">
                {k.files.map((f) => (
                  <details key={f.path}>
                    <summary className="mono small">{f.path}</summary>
                    <pre className="mono small" style={{ overflowX: 'auto' }}>
                      {f.content}
                    </pre>
                  </details>
                ))}
              </div>
            </Card>
          </>
        )}
      </QueryView>
    </div>
  );
}

/** What to tell an AI agent: the plugin skill, or the self-contained prompt to use with the ZIP. */
export function PromptCard({ kit }: { kit: WorkflowKit }) {
  const [kind, setKind] = useState<PromptKind>('plugin');
  const [copied, setCopied] = useState(false);
  const text = kind === 'plugin' ? kit.pluginPrompt : kit.prompt;
  const copy = () =>
    navigator.clipboard.writeText(text).then(
      () => setCopied(true),
      () => undefined,
    );
  return (
    <Card
      title="Prompt for your AI agent"
      subtitle={
        kind === 'plugin'
          ? 'In Claude Code with the lixionary-jira plugin, run this in the service repository. The plugin downloads this kit, adapts it to the repository and opens a pull request. In Cursor or Antigravity, use the lixionary-jira-workflows skill with the project key.'
          : 'For any AI agent: give it this prompt together with the extracted ZIP, in the service repository.'
      }
      action={
        <Segmented<PromptKind>
          label="Agent"
          value={kind}
          onChange={(v) => {
            setKind(v);
            setCopied(false);
          }}
          options={[
            { value: 'plugin', label: 'With the plugin' },
            { value: 'any', label: 'Any agent' },
          ]}
        />
      }
    >
      <pre className="mono small" style={{ overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: 360 }}>
        {text}
      </pre>
      <div>
        <Button size="sm" variant="secondary" icon={<Copy size={14} aria-hidden />} onClick={copy}>
          {copied ? 'Copied' : 'Copy prompt'}
        </Button>
      </div>
    </Card>
  );
}
